<script lang='ts'>

    /* IMPORTS --------------------------------------------------------------------------------- */

    // CASSIOPEIA
    import {
        GraphVariants,
        Span,
        Icon,
        currency,
        integer,
        percentage,
        Small,
    } from '@smatch/cassiopeia-web-components';

    import { SEASONS } from '../config/seasons';

    /* TYPES ----------------------------------------------------------------------------------- */

    import type { Product } from 'src/app/types';

    type Sizes = Product['sizes'];
    type SizeArray = NonNullable<Sizes>;

    /* EXPORTS --------------------------------------------------------------------------------- */

    export let product: Product;
    export let index: number;
    export let config: Record<string, boolean> = {};
    export let currencyCode: 'EUR' | 'USD';
    export let maxSizes: number;

    /* INTERNALS ------------------------------------------------------------------------------- */

    const firstLetterUppercase = (word: string | undefined): string => {

        if (word === undefined) {
            return '';
        }

        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    };

    const getRowClass = (index: number): string => (index % 2 === 0 ? 's8-even' : 's8-odd');

    const getGender = (genderCode: Product['genderCode']): string => firstLetterUppercase(genderCode);

    const getSeason = (season: Product['season']): string => {

        if (season?.code === undefined) {
            return '';
        }

        const MATCH = SEASONS.find(seasonOption => seasonOption.key === season.code);

        const LABEL = MATCH ? MATCH.label : season.code;

        return `${ LABEL } ${ season.year ?? '' }`.trim();
    };

    const getColor = (attributes: Product['attributes']): string => (attributes === undefined
        ? ''
        : (attributes.find(attribute => attribute.type === 'color')?.label)?.toLowerCase()
            .replace(/^\w/u, matched => matched.toUpperCase())) ?? '';

    const getVariantQuantityStyle = (quantity: number): string => '!leading-tight '
        + `${ quantity === 0 ? 'text-inherit' : 'text-grey-600' }`;

    const padSizes = (sizes: SizeArray): SizeArray => {

        const EMPTY_SIZE = { dimension: { label: '', value: '' }, quantity: 0 };
        const PADDING = Array(maxSizes - sizes.length).fill(EMPTY_SIZE) as SizeArray;

        return [ ...sizes, ...PADDING ] as SizeArray;
    };

</script>

    <div class = { `s8-cell ${ getRowClass(index) } s8-fixed s8-product` }>
        {#if Array.isArray(product.images) && product.images.length > 0}
            <span class = 's8-product-image'>
                <img src = { product.images[0].url } alt = ''/>
            </span>
        {/if}
        <span>
            <span class="s8-product-name">
                {#if product.name}
                    { product.name.replace(/(?<slash>\/)/gu, ' $<slash> ') }
                {:else}
                    { product.brand ? `${ product.brand.name } Product` : 'Product' }
                {/if}
            </span>
            <br/>
            {#if product._.key }
                <span class = 's8-product-id'>
                    {#if product.reference }
                        { product.reference }
                    {:else}
                        { product._.key.split('-').shift() }
                    {/if}
                </span>
            {/if}
            {#if product.isMatch}
                <Icon
                    key = 'heartCheck'
                    css = 'h-4 absolute right-1 top-1 stroke-quaternary'
                />
            {/if}
        </span>
    </div>

    {#if config.hasColor}
        <div class = { `s8-cell ${ getRowClass(index) } s8-color` }>
            { getColor(product.attributes) }
        </div>
    {/if}

    {#if config.hasCategory}
        <div class = { `s8-cell ${ getRowClass(index) } s8-category` }>
            {#if product.category }
                { product.category.name }
            {/if}
        </div>
    {/if}

    {#if config.hasGender}
        <div class = { `s8-cell ${ getRowClass(index) } s8-gender` }>
            { getGender(product.genderCode) }
        </div>
    {/if}

    {#if config.hasSeason}
        <div class = { `s8-cell ${ getRowClass(index) } s8-season` }>
            { getSeason(product.season) }
        </div>
    {/if}

    {#if config.hasBrand}
        <div class = { `s8-cell ${ getRowClass(index) } s8-brand` }>
            {#if product.brand } { product.brand.name } {/if}
        </div>
    {/if}

    <div class = { `s8-cell ${ getRowClass(index) } s8-count` }>
        { integer(product.total.quantity.items, false) }
        <Span label = 'Items' css = 'text-grey-600 text-xxs ml-1 mt-0.5' />
    </div>

    <div class = { `s8-cell ${ getRowClass(index) } s8-count` }>
        { currency(product.total.price.retail.recommended / product.total.quantity.items, currencyCode, false) }
        <Span label = '/ Item' css = 'whitespace-nowrap text-grey-600 text-xxs ml-1 mt-0.5' />
    </div>
    <div class = { `s8-cell ${ getRowClass(index) } s8-count` }>
        {#if product.offer !== undefined}
            { currency(product.offer.average.perItem, currencyCode, false) }
            <Span label = '/ Item' css = 'whitespace-nowrap text-grey-600 text-xxs ml-1 mt-0.5' />
        {:else}
            <Small>Not Specified</Small>
        {/if}
    </div>
    <div class = { `s8-cell ${ getRowClass(index) } s8-count` }>
        {#if product.discount !== undefined && product.discount > 0}
            { percentage(product.discount) }
            <Span label = '/ Item' css = 'whitespace-nowrap text-grey-600 text-xxs ml-1 mt-0.5' />
        {:else}
            <Small>No Discount</Small>
        {/if}
    </div>
    {#if Array.isArray(product.sizes)}

        <div class = { `s8-cell ${ getRowClass(index) } s8-graph` }>
            <GraphVariants sizes = { product.sizes } />
        </div>

        {#each padSizes(product.sizes) as { dimension, quantity }}
            <div class = { `s8-cell ${ getRowClass(index) } s8-variant` }>
                {#if dimension}
                    <span class = { quantity === 0 ? 'text-grey-300' : ''}>
                        &times;&nbsp;{ quantity }<br />
                        <!-- Undefined to ensure resiliency on bad product imports -->
                        <!-- eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -->
                        {#if dimension.value.length !== undefined}
                            <Span
                                size = { dimension.value.length > 5 ? 'sm' : 'md' }
                                css = { getVariantQuantityStyle(quantity) }
                            >
                                { dimension.value }
                            </Span>
                        {/if}
                    </span>
                {/if}
            </div>
        {/each}

    {/if}


<style lang='postcss'>

    div.s8-cell {
        @apply h-20 border-b border-r border-grey-200 flex items-center text-center;

        &.s8-fixed {
            @apply sticky left-0 z-10 border-r-0 text-left pl-2 pr-3 justify-start border-l border-grey-200;
            span.s8-product-name {
                word-break: break-word;
            }
            &:after{
                @apply absolute left-full top-0 bottom-0 w-2 opacity-10;
                background: linear-gradient(90deg, rgba(39, 39, 17, 1) 0%, rgba(39, 39, 17, 0) 100%);
                content: '';
            }
        }

        &.s8-even { @apply bg-light; }
        &:not(.s8-even) { @apply bg-grey-100; }

        &.s8-product {
            @apply min-w-[240px] leading-tight;

            &.s8-even span.s8-product-image img {
                filter: brightness(0.97) opacity(99%);
            }

            span.s8-product-image {
                @apply w-14 h-14 mr-3 block flex-none;

                img {
                    @apply w-full h-full object-cover object-center;
                }
            }

            span.s8-product-id {
                @apply inline-block w-32 text-xxs text-grey-600 text-ellipsis overflow-hidden whitespace-nowrap;
            }
        }

        &.s8-category,
        &.s8-brand,
        &.s8-gender,
        &.s8-season,
        &.s8-color, {
            @apply px-4 text-center;
        }

        &.s8-distribution {
            @apply text-center justify-center;
        }

        &.s8-count {
            @apply px-4 justify-end;
        }

        &.s8-graph,
        &.s8-variant {
            @apply justify-center px-4;
        }
    }

</style>